import * as React from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import { isServer } from '../../../lib/utils'
import { Button } from '../../UI/Button'
import { HeaderData } from '../types'
import { DropdownLink } from './DropdownLink'
import CustomLink from '../../CustomLink'
import { useI18n } from '../../../lib/context/i18n'
import { OnboardingWizzard } from '../../OnboardingWizzard'

const Backdrop = styled.div`
  background-color: rgba(255, 255, 255, 0.8);
  height: 100%;
  left: 0;
  position: absolute;
  top: 0;
  width: 100%;
  z-index: 10;
`

const DropdownCard = styled.div`
  background: ${p => p.theme.colors.white[1]};
  border-radius: 0.5rem;
  box-shadow: ${p => p.theme.shadows.menuOpen};
  height: 100%;
  max-width: 22rem;
  overflow: hidden;
  overscroll-behavior: contain;
  position: relative;
  transform-origin: top right;
  transform: scale(0.98);
  transition: transform 0.2s ease-in-out;
  width: 100%;
  z-index: 11;
`

const DropdownWrapper = styled.div<{
  active?: boolean
}>`
  display: flex;
  height: 100%;
  justify-content: flex-end;
  opacity: ${({ active }) => (active ? 1 : 0)};
  overflow: hidden;
  overscroll-behavior: contain;
  padding: 0.4rem;
  position: fixed;
  right: 0;
  top: 0;
  transition: all 0.3s ease-in-out;
  visibility: ${({ active }) => (active ? 'visible' : 'hidden')};
  width: 100%;
  z-index: 9999;

  &.DropdownWrapper--isOpen ${DropdownCard} {
    transform: scale(1);
  }
`

const ParentNavigation = styled.div`
  align-items: stretch;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  height: 100%;
  justify-content: center;
  padding: 2rem;
`

const GroupWrapper = styled.nav`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const ButtonWrapper = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
`

const Divider = styled.hr`
  background: ${p => p.theme.colors.black[6]};
  border: 0;
  height: 1px;
  margin: 0 auto;
  max-width: 6rem;
  width: 100%;
`

export const Dropdown = ({ data }: { data: HeaderData }) => {
  const [isOpen, setIsOpen] = React.useState(false)
  const { getTranslatedUrlFromLink } = useI18n()

  const toggleMenu = React.useCallback(() => {
    setIsOpen(!isOpen)
  }, [isOpen])

  const closeMenu = React.useCallback(() => {
    setIsOpen(false)
  }, [])

  React.useEffect(() => {
    const handleBodyLock = () => {
      if (isOpen && window.innerWidth <= 1400) {
        document.body.style.overflow = 'hidden'
      } else {
        document.body.style.overflow = 'auto'
      }
    }

    handleBodyLock()
    window && window.addEventListener('resize', handleBodyLock)
    return () => {
      window && window.removeEventListener('resize', handleBodyLock)
    }
  }, [isOpen])

  const DropdownPortal = !isServer()
    ? createPortal(
      <DropdownWrapper className={isOpen ? 'DropdownWrapper--isOpen' : ''} active={isOpen}>
        <Backdrop onClick={closeMenu}></Backdrop>
        <DropdownCard>
          <ButtonWrapper>
            <Button iconLeft="close" onClick={closeMenu} $size="small" $variant="tertiary" />
          </ButtonWrapper>
          <ParentNavigation>
            <GroupWrapper>
              {data.primary_navigation.map(navItem => (
                <DropdownLink
                  to={navItem.link && getTranslatedUrlFromLink(navItem.link)}
                  label={navItem.label}
                  key={navItem._uid}
                  content={navItem.dropdown}
                  onClick={closeMenu}
                  parentIsOpen={isOpen}
                />
              ))}
            </GroupWrapper>
            <Divider />
            <div>
              <GroupWrapper>
                {data.secondary_navigation.map(navItem => (
                  <DropdownLink
                    to={navItem.link && getTranslatedUrlFromLink(navItem.link)}
                    label={navItem.label}
                    key={navItem._uid}
                    content={navItem.dropdown}
                    onClick={closeMenu}
                    parentIsOpen={isOpen}
                  />
                ))}
                {data?.contact_button[0] && (
                  <div>
                    <Button
                      as={CustomLink}
                      to={getTranslatedUrlFromLink(data.contact_button[0].link)}
                      onClick={closeMenu}
                      $size="small"
                      $ignoreTheme
                    >
                      {data.contact_button[0].label}
                    </Button>
                  </div>
                )}
              </GroupWrapper>
            </div>
          </ParentNavigation>
        </DropdownCard>
      </DropdownWrapper>,
      document.body,
    )
    : null

  return (
    <>
      <Button
        $variant="tertiary"
        $size="small"
        iconLeft={isOpen ? 'close' : 'menu'}
        $ignoreTheme
        onClick={toggleMenu}
      ></Button>
      {DropdownPortal}
    </>
  )
}
